import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { TrendingUp, TrendingDown, History } from 'lucide-react';
import { taostats } from '../api/taoStats';
import { lineChartOptions } from '../utils/chartHelpers';
import { DelegationEvents } from './DelegationEvents';

interface SubnetChartProps {
  coldkey: string | undefined;
  hotkey: string | undefined;
  netuid: string;
  subnetName: string;
  block_start?: number;
  onCostBasisCalculated?: (costBasis: {
    totalTaoSpent: number;
    totalTaoReceived: number;
    totalAlphaReceived: number;
    totalAlphaSpent: number;
    pnl: number;
    pnlPercentage: number;
    currentTaoValue: number;
  }) => void;
}

interface PriceChange {
  value: number;
  timeframe: string;
}

interface BalanceHistoryPoint {
  block_number: number;
  timestamp: string;
  balance: string;
  balance_as_tao: string;
}

export function SubnetChart({ 
  coldkey, 
  hotkey, 
  netuid, 
  subnetName,
  block_start,
  onCostBasisCalculated 
}: SubnetChartProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<BalanceHistoryPoint[]>([]);
  const [priceChanges, setPriceChanges] = useState<PriceChange[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [lastZeroBalance, setLastZeroBalance] = useState<BalanceHistoryPoint | null>(null);
  const [currentTaoValue, setCurrentTaoValue] = useState(0);
  const [dataFetched, setDataFetched] = useState(false);

  // Memoized function to calculate price change
  const calculatePriceChange = useCallback((sortedData: BalanceHistoryPoint[], hours: number): number | null => {
    if (sortedData.length < 2) return null;
    
    const currentValue = Number(BigInt(sortedData[0].balance_as_tao)) / 1e9;
    const timeAgo = new Date(Date.now() - hours * 60 * 60 * 1000);
    
    const previousDataPoint = sortedData.find(point => 
      new Date(point.timestamp) <= timeAgo
    ) || sortedData[sortedData.length - 1];
    
    const previousValue = Number(BigInt(previousDataPoint.balance_as_tao)) / 1e9;
    
    if (previousValue > 0) {
      return ((currentValue - previousValue) / previousValue) * 100;
    }
    
    return null;
  }, []);

  // Fetch data only once
  useEffect(() => {
    const fetchData = async () => {
      if (!coldkey || !hotkey || dataFetched) {
        if (!coldkey || !hotkey) {
          setError('Invalid wallet or hotkey address');
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log(`Fetching historical data for subnet ${netuid} (coldkey: ${coldkey}, hotkey: ${hotkey})`);

        // Fetch historical data
        const response = await taostats.getHistoricalStakeBalance({
          coldkey,
          hotkey,
          netuid: parseInt(netuid),
          limit: 30 // Limit to 30 data points
        });

        // Sort data by timestamp in descending order
        const sortedData = response.data.sort((a, b) => 
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );

        console.log(`Received ${sortedData.length} historical data points for subnet ${netuid}`);
        setData(sortedData);
        setDataFetched(true);
        
        // Set current TAO value for PnL calculation
        if (sortedData.length > 0) {
          const currentValue = Number(BigInt(sortedData[0].balance_as_tao)) / 1e9;
          setCurrentTaoValue(currentValue);
        }
        
        // Find the most recent point where balance was 0
        const zeroBalancePoint = sortedData.find(point => 
          Number(BigInt(point.balance_as_tao)) === 0
        );
        
        if (zeroBalancePoint) {
          setLastZeroBalance(zeroBalancePoint);
          console.log(`Found zero balance point for subnet ${netuid} at block ${zeroBalancePoint.block_number}`);
        }
        
        // Calculate price changes
        if (sortedData.length > 0) {
          const changes: PriceChange[] = [
            { value: calculatePriceChange(sortedData, 6) || 0, timeframe: '6h' },
            { value: calculatePriceChange(sortedData, 12) || 0, timeframe: '12h' },
            { value: calculatePriceChange(sortedData, 24) || 0, timeframe: '24h' }
          ];
          
          setPriceChanges(changes);
        }
      } catch (error) {
        console.error('Failed to fetch historical data:', error);
        setError('Failed to load historical data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [coldkey, hotkey, netuid, calculatePriceChange, dataFetched]);

  // Memoize chart data to prevent unnecessary recalculations
  const chartData = useMemo(() => {
    if (data.length === 0) return null;
    
    return {
      labels: data.map(item => new Date(item.timestamp)).reverse(),
      datasets: [
        {
          label: 'Balance (τ)',
          data: data.map(item => Number(BigInt(item.balance_as_tao)) / 1e9).reverse(),
          borderColor: 'rgba(239, 68, 68, 1)',
          backgroundColor: 'rgba(239, 68, 68, 0.2)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
        }
      ],
    };
  }, [data]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-2">
        <div className="h-4 bg-gray-700/50 rounded w-24"></div>
        <div className="h-6 bg-gray-700/50 rounded w-32"></div>
        <div className="h-[150px] bg-gray-700/50 rounded"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-400 text-sm">{error}</div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="text-gray-400 text-sm">No historical data available</div>
    );
  }

  // Calculate current balance
  const currentBalance = Number(BigInt(data[0].balance)) / 1e9;
  const currentTaoBalance = Number(BigInt(data[0].balance_as_tao)) / 1e9;

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm text-gray-400">Current Balance</p>
          <div className="flex items-baseline space-x-2">
            <p className="text-lg font-bold">{currentTaoBalance.toFixed(4)} τ</p>
            <p className="text-sm text-gray-400">({currentBalance.toFixed(4)} α)</p>
          </div>
        </div>
        <div className="flex space-x-2">
          {priceChanges.map((change, index) => (
            <div key={index} className="text-xs px-2 py-1 rounded-lg bg-black/20">
              <div className="flex items-center space-x-1">
                {change.value > 0 ? (
                  <TrendingUp className="w-3 h-3 text-green-400" />
                ) : (
                  <TrendingDown className="w-3 h-3 text-red-400" />
                )}
                <span className={change.value > 0 ? 'text-green-400' : 'text-red-400'}>
                  {Math.abs(change.value).toFixed(1)}%
                </span>
                <span className="text-gray-400">{change.timeframe}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="h-[150px]">
        {chartData && <Line data={chartData} options={lineChartOptions} />}
      </div>

      <DelegationEvents
        coldkey={coldkey || ''}
        hotkey={hotkey || ''}
        netuid={parseInt(netuid)}
        block_start={block_start}
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded(!isExpanded)}
        onCostBasisCalculated={onCostBasisCalculated}
        currentTaoValue={currentTaoBalance}
      />
    </div>
  );
}